import { HStack, Tooltip, VStack } from '@chakra-ui/react';
import {
  MetricsTableData,
  findParentRow,
  isFrequencyYearly,
  useAggregatedRowValue,
  usePercentageValues,
} from '../../../MetricAnswers.hooks';
import { Typography } from 'Tokens';
import {
  TimePeriods,
  TimePeriodsEnums,
} from 'containers/Esrs/pieces/DisclosureRequirements/Requirement';
import { useMemo, useState } from 'react';
import { GeneratedAnswer } from '../../../MetricAI';
import { useGetDatapointValues } from '../InputFields/QuantitativeInputs';
import { WarningIcon } from 'Tokens/Icons/Status';
import { QuestionType_Enum_ } from 'models';
import { Button } from 'Atoms';

export const PercentageValueView = ({ value, row }: { value: string; row: MetricsTableData }) => {
  const tags = useMemo(() => {
    return row?.tags?.map((x) => x) ?? [];
  }, [row]);

  if (value)
    return (
      <Tooltip
        label={`${row.metric.title} ${tags.length ? ' by ' : ''} ${tags.map(
          (x) => x.tagValue
        )}: ${value.replace('(', '').replace(')', '')}`}
        cursor="pointer"
      >
        <span style={{ cursor: 'pointer', fontSize: '14px', marginLeft: '4px' }}> {value}</span>
      </Tooltip>
    );

  return null;
};

const ToggleShowMoreButton = ({
  showMore,
  setShowMore,
}: {
  showMore: boolean;
  setShowMore: (value: boolean) => void;
}) => (
  <Button variant="ghost" size="sm" alignVertically={true} onClick={() => setShowMore(!showMore)}>
    {showMore ? 'Show less' : 'Show more'}
  </Button>
);

const generatedTextWidth = 400;
const currentTextWidth = 500;
const avgCharWidth = 7;
const maxCharsPerLineGenText = Math.floor(generatedTextWidth / avgCharWidth);
const maxCharsPerLineCurrText = Math.floor(currentTextWidth / avgCharWidth);

export const MetricAIAnswer = ({
  row,
  companyReportingUnit,
  nestedMetrics,
  generatedAnswer,
  isAI,
  selectedQuarter,
}: {
  row: MetricsTableData;
  companyReportingUnit?: string;
  nestedMetrics: MetricsTableData[];
  generatedAnswer?: GeneratedAnswer;
  isAI?: boolean;
  selectedQuarter: TimePeriods;
}) => {
  const { dataPointsPerQuarter, dataPointPerYear, isAggregated } = useGetDatapointValues(
    row,
    companyReportingUnit
  );

  const [showMore, setShowMore] = useState(false);

  const isYearly = isFrequencyYearly(row.metric);

  const isNarrativeMetric = useMemo(
    () => row.metric.metricType !== QuestionType_Enum_.Decimal_,
    [row]
  );

  const { result: aggregatedValues } = useAggregatedRowValue(row, isYearly, companyReportingUnit);

  const showAsPercentage = useMemo(() => {
    if (isAI) return false;
    return row.metric.showAsPercentage ?? false;
  }, [row]);

  const quarterValue = useMemo(() => {
    return dataPointsPerQuarter.find((dp) => dp.field === selectedQuarter)?.value;
  }, [dataPointsPerQuarter]);

  const quartersTotal = useMemo(() => {
    const quartersValues = dataPointsPerQuarter.map((dp) => dp.value?.value);
    return quartersValues.reduce((a, b) => Number(a ?? 0) + Number(b ?? 0), 0);
  }, [dataPointsPerQuarter]);

  const aggregatedValue = useMemo(() => {
    if (!isYearly && selectedQuarter !== TimePeriodsEnums.year) {
      const value = aggregatedValues[selectedQuarter];
      return value !== undefined && !isNaN(Number(value)) ? Number(Number(value).toFixed(2)) : '—';
    }

    const yearValue = aggregatedValues.Year;
    return yearValue !== undefined && !isNaN(yearValue) ? Number(yearValue.toFixed(2)) : '—';
  }, [aggregatedValues, isYearly, selectedQuarter]);

  const rowValue = useMemo(() => {
    // check for quarter value
    if (!isYearly && selectedQuarter !== TimePeriodsEnums.year) {
      return quarterValue?.value ?? 0;
    }

    // yearly or narrative metric => show year value
    if (isYearly || isNarrativeMetric) {
      return dataPointPerYear?.value ?? (isNarrativeMetric ? 'Answer' : 0);
    }

    // quarterly and selected year => show quarters total
    return quartersTotal;
  }, [isYearly, quartersTotal, isNarrativeMetric, selectedQuarter, dataPointPerYear, quarterValue]);

  const percentageValue = usePercentageValues(
    findParentRow(nestedMetrics, row),
    row,
    selectedQuarter,
    isYearly,
    isAggregated ? aggregatedValue : rowValue
  );

  const isInvalidAnswer = useMemo(
    () => generatedAnswer?.answer === 'NA' || generatedAnswer?.answer === '-',
    [generatedAnswer]
  );

  const isLongTextParentMetric = useMemo(
    () =>
      row.metric.metricType === QuestionType_Enum_.LongText_ &&
      !!row.metric.childrenMetrics?.length,
    [row.metric]
  );

  const generatedTextNumberOfLines = useMemo(
    () => Math.ceil((generatedAnswer?.answer.length ?? 1) / maxCharsPerLineGenText),
    [generatedAnswer]
  );

  const currentTextNumberOfLines = useMemo(
    () => Math.ceil((dataPointPerYear?.value?.length ?? 1) / maxCharsPerLineCurrText),
    [dataPointPerYear]
  );

  if (isLongTextParentMetric) {
    return null;
  }

  if (generatedAnswer) {
    return (
      <VStack alignItems="start" w={`${generatedTextWidth}px`}>
        <Typography variant="body" width="100%" noOfLines={showMore ? undefined : 5}>
          {!isInvalidAnswer ? generatedAnswer?.answer : '—'}
        </Typography>
        {/* For long text */}
        {generatedTextNumberOfLines > 5 && (
          <ToggleShowMoreButton showMore={showMore} setShowMore={setShowMore} />
        )}
        {isInvalidAnswer && (
          <HStack spacing="4px" alignItems="flex-start" justifyContent="start">
            <WarningIcon color="text.warning" boxSize="18px"></WarningIcon>
            <Typography variant="body" color="text.warning">
              AI couldn't generate answer
            </Typography>
          </HStack>
        )}
      </VStack>
    );
  }

  if (isAggregated && !isNarrativeMetric)
    return (
      <Typography variant="body" width="350px">
        {aggregatedValue}{' '}
        {showAsPercentage ? <PercentageValueView row={row} value={percentageValue} /> : ''}
      </Typography>
    );

  return (
    <VStack w={`${currentTextWidth}px`} alignItems="start">
      <Typography
        color={isAI && !dataPointPerYear?.value ? 'text.hint' : ''}
        variant="body"
        width="100%"
        noOfLines={showMore ? undefined : 5}
      >
        {rowValue}
        {showAsPercentage && <PercentageValueView row={row} value={percentageValue} />}
      </Typography>

      {/* For long text */}
      {currentTextNumberOfLines > 5 && (
        <ToggleShowMoreButton showMore={showMore} setShowMore={setShowMore} />
      )}
    </VStack>
  );
};
